import { useState } from 'react';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Alert, Button, Typography } from '@mui/material';
import CostOfWork from './CostOfWork';
import { handleInsertSubcodeDocs } from '../uploadSubcodeDocs';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import {
  resetUccForm,
  updateFire
} from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import Summary from './Summary';
import getEnvVars from 'src/config/env';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Custom tab panel component
function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Function to generate accessibility props for tabs
type A11yProps = (index: number) => { id: string; 'aria-controls': string };
const a11yProps: A11yProps = (index) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`
});

const FireSubcode = ({
  applicationId,
  UCCFormShareID = 0,
  townshipID = 0,
  setExpanded,
  isGuest= false,
  fetchFormStatuses
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const { applicationID, townshipId } = useParams();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const subCodesFormData = useSelector(
    (state: RootState) => state.constructionFormSlice.Fire
  );
  const { socketUrl } = getEnvVars();
  // Handles form field changes and updates the redux store
  const handleChange = (val: string, e: any) => {
    const { name } = e;
    dispatch(updateFire({ [name]: val }));
  };
  const navigate = useNavigate();

  // List of form fields
  const allFields: Fields[] = [
    {
      fieldName: 'ContractorName',
      initialValue: subCodesFormData?.ContractorName || '',
      fieldLabel: 'Contractor Name',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorEmail',
      initialValue: subCodesFormData?.ContractorEmail || '',
      fieldLabel: 'Contractor Email Address',
      fieldType: 'email',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorPhoneNumber',
      initialValue: subCodesFormData?.ContractorPhoneNumber || '',
      fieldLabel: 'Contractor Phone Number',
      fieldType: 'mobile',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorAddress',
      initialValue: subCodesFormData?.ContractorAddress || '',
      fieldLabel: 'Contractor Address',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorType',
      initialValue: subCodesFormData?.ContractorType || '',
      fieldLabel: 'Contractor Type',
      fieldType: 'select',
      options: ['Street', 'Municipality'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'UtilityCompany',
      initialValue: subCodesFormData?.UtilityCompany || '',
      fieldLabel: 'Utility Company',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FederalID',
      initialValue: subCodesFormData?.FederalID || '',
      fieldLabel: 'Federal Employer ID Number (EIN)',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorLicenseNumber',
      initialValue: subCodesFormData?.ContractorLicenseNumber || '',
      fieldLabel: 'Contractor License Number',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ExpiryDate',
      initialValue: subCodesFormData?.ExpiryDate || '',
      fieldLabel: 'Contractor License Expiration Date',
      required: true,
      fieldType: 'date',
      dependency: (val: string) => dispatch(updateFire({ ExpiryDate: val }))
    },
    {
      fieldName: 'WaterSupplySource',
      initialValue: subCodesFormData?.WaterSupplySource || '',
      fieldLabel: 'Water Supply Source',
      fieldType: 'text',
      required: true,
      dependency: handleChange,
      heading: 'FIRE PROTECTION CHARACTERISTICS'
    },
    {
      fieldName: 'MethodOfAlarm',
      initialValue: subCodesFormData?.MethodOfAlarm || '',
      fieldLabel: 'Method of Alarm/Suppression System Supervision',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'HeatingSystem',
      initialValue: subCodesFormData?.HeatingSystem || '',
      fieldLabel: 'Heating System',
      fieldType: 'select',
      options: ['New', 'Modification to Existing', 'Conversion', 'Replacement'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FuelType',
      initialValue: subCodesFormData?.FuelType || '',
      fieldLabel: 'Fuel Type',
      fieldType: 'select',
      options: ['Gas', 'Solar', 'Oil', 'Electric', 'Other'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FuelStorageTankType',
      initialValue: subCodesFormData?.FuelStorageTankType || '',
      fieldLabel: 'Fuel Storage Tank Type',
      fieldType: 'select',
      options: ['Flammable', 'Combustible'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'Capacity',
      initialValue: subCodesFormData?.Capacity || '',
      fieldLabel: 'Storage Tank Capacity',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FireAlarmSystem',
      initialValue: subCodesFormData?.FireAlarmSystem || '',
      fieldLabel: 'Fire Alarm System',
      fieldType: 'select',
      options: ['New', 'Existing'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FireSuppression',
      initialValue: subCodesFormData?.FireSuppression || '',
      fieldLabel: 'Fire Suppression/Standpipe System',
      fieldType: 'select',
      options: ['New', 'Existing'],
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'MainControlValveLocation',
      initialValue: subCodesFormData?.MainControlValveLocation || '',
      fieldLabel: 'Location of Main Control Valve',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'DescriptionOfWork',
      initialValue: subCodesFormData?.DescriptionOfWork || '',
      fieldLabel: 'Description of Work',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FireDocumentFile',
      fieldLabel: 'Upload Document',
      initialValue: subCodesFormData?.FireDocumentFile || '',
      fieldType: 'file',
      listUI: true,
      placement: 'start',
      dependency: handleChange,
      aboveParagraph: (
        <Typography>
          Please upload scanned copy or photo of the Federal Employer ID Number
          (EIN) license. Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF
          files are allowed, with maximum file size of 10MB.
        </Typography>
      ),
      belowParagraph: subCodesFormData?.DocumentOfContractorIdentity && (
        <a
          target="_blank"
          rel="noreferrer"
          href={socketUrl + subCodesFormData?.DocumentOfContractorIdentity}
        >
          Federal Employer ID Number (EIN) license
        </a>
      )
    }
  ];

  async function insertFireSubcode(payload: any) {
    const response = await httpRequest(
      'POST',
      `/construction/fireProtection/ucc/${applicationId}`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Fire UCC form created successfully.`);
      dispatch(
        updateFire({ UCCFireProtectionID: response?.data?.UCCFireProtectionID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      if (isGuest === true) {
        // For guest users, redirect to confirmation page
        // You may need to adjust the route based on your routing setup
        navigate('/confirmation', { 
          state: { 
            formType: 'Fire UCC Form',
            email: payload.email 
          },
          replace: true // This prevents going back with the browser back button
        });
      } else {
        // For regular users, just reset the form
        setValue(0);
      }
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function updateFireSubcode(payload: any) {
    const response = await httpRequest(
      'PATCH',
      `/construction/fireProtection/ucc/${
        subCodesFormData?.UCCFireProtectionID
      }/${applicationId || applicationID}`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Fire UCC form updated successfully.`);
      dispatch(
        updateFire({ UCCFireProtectionID: response?.data?.UCCFireProtectionID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      setValue(0);
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function handleSaveForm() {
    let FileData = { FilePath: '' };
    if (subCodesFormData?.FireDocumentFile) {
      FileData = await handleInsertSubcodeDocs(
        subCodesFormData?.FireDocumentFile[0],
        'Construction',
        'Fire'
      );
    }

    const payload = {
      UserID: userDetails?.UserID || 0,
      SelfFilled: userDetails?.UserID ? true : false,
      SignatureImage: subCodesFormData?.SignatureImage,
      DocumentOfContractorIdentity: FileData?.FilePath,
      ContractorEmail: subCodesFormData?.ContractorEmail,
      ContractorType: subCodesFormData?.ContractorType,
      DescriptionOfWork: subCodesFormData?.DescriptionOfWork,
      WaterSupplySource: subCodesFormData?.WaterSupplySource,
      FuelStorageTankType: subCodesFormData?.FuelStorageTankType,
      Capacity: subCodesFormData?.Capacity,
      MainControlValveLocation: subCodesFormData?.MainControlValveLocation,
      FireSuppression: subCodesFormData?.FireSuppression,
      FireAlarmSystem: subCodesFormData?.FireAlarmSystem,
      MethodOfAlarm: subCodesFormData?.MethodOfAlarm,
      ExpiryDate: subCodesFormData?.ExpiryDate,
      ContractorLicenseNumber: subCodesFormData?.ContractorLicenseNumber,
      ContractorPhoneNumber: subCodesFormData?.ContractorPhoneNumber,
      ContractorAddress: subCodesFormData?.ContractorAddress,
      EstimatedCostOfFireProtectionWork: subCodesFormData?.EstimatedCostOfFireProtectionWork,
      ContractorName: subCodesFormData?.ContractorName,
      TotalFees: subCodesFormData?.getAllFee?.totalFee,
      UtilityCompany: subCodesFormData?.UtilityCompany,
      HeatingSystem: subCodesFormData?.HeatingSystem,
      WorkItems: subCodesFormData?.WorkItems || [],
      FederalID: subCodesFormData?.FederalID,
      TownshipID: townshipId || townshipID,
      FuelType: subCodesFormData?.FuelType,
      UCCFormShareID: UCCFormShareID
    };
    if (subCodesFormData?.UCCFireProtectionID) {
      updateFireSubcode(payload);
    } else insertFireSubcode(payload);
    setValue(2);
  }

  function onNext() {
    setValue(value + 1);
  }

  function onBack() {
    setValue(value - 1);
  }
  // Add this function after your existing handleChange function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  setValue(newValue);
  };
  
  return (
    <>
      <Box sx={{ width: '100%' }}>
        {/* Tabs */}
        <Tabs value={value} onChange={handleTabChange} aria-label="Construction Permit Tabs">
          <Tab label="Fire Subcode" {...a11yProps(0)} />
          <Tab label="Cost of Work" {...a11yProps(1)} />
          <Tab label="Summary of Costs" {...a11yProps(2)} />
        </Tabs>

        <Alert severity="info" sx={{ mt: 2 }}>
          Provide information for whoever will be performing Fire work.
        </Alert>

        {/* Tab 1 - Fire Subcode Form */}
        <CustomTabPanel value={value} index={0}>
          <FormikForm
            allFields={allFields}
            defaultEnabledButton={['Save']}
            buttonTitle={['Cancel', 'Save']}
            heading={'Construction Permit'}
            buttonPlacement={'right'}
            formMethod={onNext}
            handleOtherButton={() => setExpanded(false)}
          />
        </CustomTabPanel>

        {/* Tab 2 - Estimated Cost of Work Form */}
        <CustomTabPanel value={value} index={1}>
          <CostOfWork
            EstimatedCostOfFireProtectionWork={subCodesFormData?.EstimatedCostOfFireProtectionWork}
            onBack={onBack}
            onNext={onNext}
            townshipID={townshipID}
          />
        </CustomTabPanel>

        {/* Tab 3 - Summary of Costs */}
        <CustomTabPanel value={value} index={2}>
          <Summary />

          {/* Form Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3
            }}
          >
            <Button onClick={onBack} variant="contained" color="inherit">
              Back
            </Button>
            <Button onClick={handleSaveForm} variant="contained">
              {subCodesFormData?.UCCFireProtectionID ? 'Update' : 'Save'}
            </Button>
          </Box>
        </CustomTabPanel>
      </Box>
    </>
  );
};

// Add display name for debugging purposes
FireSubcode.displayName = 'FireSubcode';

export default FireSubcode;
